import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ToasterService } from '@navfleet/client/shared/services/toast';
import { TranslationService } from '@navfleet/shared/core/translation';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { Subscription, filter, take, timer } from 'rxjs';

export const APP_UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;
export const APP_UPDATE_INITIAL_CHECK_DELAY_MS = 10 * 1000;
export const APP_UPDATE_UNRECOVERABLE_REMINDER_INTERVAL_MS = 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
    private readonly swUpdate = inject(SwUpdate);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly toasterService = inject(ToasterService);
    private readonly translationService = inject(TranslationService);
    private readonly ngZone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);

    private reminder?: Subscription;
    private poll?: Subscription;

    public init(): void {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        this.swUpdate.versionUpdates
            .pipe(
                filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.ngZone.run(() => this.showToast()));

        this.swUpdate.unrecoverable
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.ngZone.run(() => this.showConfirmationDialog()));

        this.pollForUpdates();
    }

    public reload(): void {
        this.reminder?.unsubscribe();
        document.location.reload();
    }

    private showToast(): void {
        this.poll?.unsubscribe();

        this.toasterService.info('APP_UPDATE.AVAILABLE_DETAIL', undefined, {
            summary: this.translationService.instant('APP_UPDATE.AVAILABLE_SUMMARY'),
            sticky: true,
            closable: false,
            data: {
                onClick: () => this.reload(),
            },
        });
    }

    private showConfirmationDialog(): void {
        this.confirmationService.confirm({
            header: this.translationService.instant('APP_UPDATE.UNRECOVERABLE_HEADER'),
            message: this.translationService.instant('APP_UPDATE.UNRECOVERABLE_MESSAGE'),
            closable: false,
            acceptLabel: 'APP_UPDATE.RELOAD_NOW',
            rejectLabel: 'APP_UPDATE.RELOAD_LATER',
            accept: () => this.reload(),
            reject: () =>
                this.remindWith(APP_UPDATE_UNRECOVERABLE_REMINDER_INTERVAL_MS, () => this.showConfirmationDialog()),
        });
    }

    private pollForUpdates(): void {
        this.poll = timer(APP_UPDATE_INITIAL_CHECK_DELAY_MS, APP_UPDATE_CHECK_INTERVAL_MS)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => void this.swUpdate.checkForUpdate());
    }

    private remindWith(delayMs: number, prompt: () => void): void {
        this.reminder?.unsubscribe();
        this.reminder = timer(delayMs)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => prompt());
    }
}
